import { Box, Link, List, ListItem, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

/**
 * Privacy policy (spec §10 + §17).
 *
 * Placeholder copy aligned with the privacy commitments documented in
 * spec §10 (consent flags, behavioral-targeting opt-out, advertiser
 * data sharing opt-out, account deletion). Final wording must be
 * reviewed by counsel before launch — see TODO(legal-review).
 */
export function PrivacyPage() {
  return (
    <Stack spacing={3} sx={{ maxWidth: 'md' }}>
      <Typography variant="h1" component="h1">
        Privacy policy
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Last updated: 2026-05-23. Placeholder text pending legal review.
      </Typography>

      <Box component="section" aria-labelledby="summary">
        <Typography id="summary" variant="h2" component="h2" sx={{ fontSize: '1.4rem', mb: 1 }}>
          Summary
        </Typography>
        <Typography variant="body1">
          Round Recon does not process payments, store payment information, or share your
          identifiable purchase activity with retailers or advertisers. Behavioral targeting,
          analytics, personalization, marketing email, and advertiser data sharing are each
          off-by-default and individually controllable from your{' '}
          <Link component={RouterLink} to="/dashboard/privacy">
            privacy dashboard
          </Link>
          .
        </Typography>
      </Box>

      <Box component="section" aria-labelledby="what-we-collect">
        <Typography
          id="what-we-collect"
          variant="h2"
          component="h2"
          sx={{ fontSize: '1.4rem', mb: 1 }}
        >
          What we collect
        </Typography>
        <List sx={{ pl: 2, listStyleType: 'disc' }}>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            <strong>Account information</strong> — your email address and a hashed password if you
            choose to register. We never store the plaintext password.
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            <strong>Saved searches, alerts, and watchlists</strong> — to deliver the notifications
            you ask for.
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            <strong>Search activity</strong> — used only to compute aggregated, anonymized market
            statistics. Behavioral targeting is off unless you opt in.
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            <strong>Technical logs</strong> — request timestamps, IP addresses, and user agent
            strings, retained per our security retention policy.
          </ListItem>
        </List>
      </Box>

      <Box component="section" aria-labelledby="what-we-dont-collect">
        <Typography
          id="what-we-dont-collect"
          variant="h2"
          component="h2"
          sx={{ fontSize: '1.4rem', mb: 1 }}
        >
          What we do not collect
        </Typography>
        <List sx={{ pl: 2, listStyleType: 'disc' }}>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            Payment card numbers, bank details, or other payment instruments.
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            Government identification documents.
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            Sensitive demographic categories (race, religion, sexual orientation, etc.).
          </ListItem>
        </List>
      </Box>

      <Box component="section" aria-labelledby="cookies">
        <Typography id="cookies" variant="h2" component="h2" sx={{ fontSize: '1.4rem', mb: 1 }}>
          Cookies and similar technologies
        </Typography>
        <Typography variant="body1">
          We use a small number of strictly-necessary cookies to keep you logged in and remember
          your consent choices. Analytics, personalization, and advertising cookies are loaded only
          after you opt in via the consent banner. See our{' '}
          <Link component={RouterLink} to="/dashboard/privacy">
            privacy dashboard
          </Link>{' '}
          to change your choices at any time.
        </Typography>
      </Box>

      <Box component="section" aria-labelledby="advertisers">
        <Typography id="advertisers" variant="h2" component="h2" sx={{ fontSize: '1.4rem', mb: 1 }}>
          Advertisers and aggregated reporting
        </Typography>
        <Typography variant="body1">
          Advertisers may purchase sponsored placements that appear alongside organic search
          results. Sponsored listings are always labeled and follow the same product-relevance and
          freshness rules as organic results. Advertisers receive only aggregated, small-cohort-
          suppressed reports — never individual user-level data — and behavioral profiles are
          excluded entirely if you have opted out of advertiser data sharing.
        </Typography>
      </Box>

      <Box component="section" aria-labelledby="rights">
        <Typography id="rights" variant="h2" component="h2" sx={{ fontSize: '1.4rem', mb: 1 }}>
          Your rights
        </Typography>
        <List sx={{ pl: 2, listStyleType: 'disc' }}>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            <strong>Export</strong> — download a copy of your account data from{' '}
            <Link component={RouterLink} to="/dashboard">
              your dashboard
            </Link>
            .
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            <strong>Delete</strong> — permanently delete your account and identifiable data from the
            same dashboard. Audit-log entries that reference your account are retained per the
            security retention policy but no longer tied to identifying fields.
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            <strong>Change consent</strong> — analytics, personalization, behavioral advertising,
            advertiser data sharing, and marketing email are each independently controllable from
            the privacy dashboard.
          </ListItem>
          <ListItem sx={{ display: 'list-item', pl: 0 }}>
            <strong>Contact us</strong> — privacy@roundrecon.com.
          </ListItem>
        </List>
      </Box>

      <Box component="section" aria-labelledby="retention">
        <Typography id="retention" variant="h2" component="h2" sx={{ fontSize: '1.4rem', mb: 1 }}>
          Retention
        </Typography>
        {/* TODO(legal-review): finalize retention windows for technical
            logs, audit-log entries, behavioral signals, and email
            messages. Counsel to provide region-specific lengths
            (CCPA, GDPR, etc.). */}
        <Typography variant="body1">
          We retain account data for as long as your account is active, plus the minimum window
          required by law and to operate the service. Aggregated, anonymized data may be retained
          indefinitely.
        </Typography>
      </Box>

      <Box component="section" aria-labelledby="changes-privacy">
        <Typography
          id="changes-privacy"
          variant="h2"
          component="h2"
          sx={{ fontSize: '1.4rem', mb: 1 }}
        >
          Changes
        </Typography>
        <Typography variant="body1">
          Material updates will be announced on the site and via the email address on your account,
          if any. Continued use after a change constitutes acceptance.
        </Typography>
      </Box>
    </Stack>
  );
}

export default PrivacyPage;
